class HeroBannerV2 extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-hero-v2-slide]'));
    if (!this.slides.length) return;

    this.track = this.querySelector('[data-hero-v2-track]');
    this.dots = Array.from(this.querySelectorAll('[data-hero-v2-dot]'));
    this.previousButton = this.querySelector('[data-hero-v2-previous]');
    this.nextButton = this.querySelector('[data-hero-v2-next]');
    this.status = this.querySelector('[data-hero-v2-status]');
    this.statusTemplate = this.status?.dataset.statusTemplate || 'Slide __index__ of __length__';
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.autoplayDelay = Math.max(2000, Number(this.dataset.autoplayDelay) || 5000);
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.loop = this.dataset.loop === 'true';
    this.isHovered = false;
    this.hasFocus = false;
    this.isVisible = true;
    this.editorPaused = false;
    this.touchStartX = null;
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileViewport = window.matchMedia('(max-width: 749px)');

    this.handleClick = (event) => {
      if (event.target.closest('[data-hero-v2-previous]')) {
        this.goTo(this.currentIndex - 1, true);
        return;
      }

      if (event.target.closest('[data-hero-v2-next]')) {
        this.goTo(this.currentIndex + 1, true);
        return;
      }

      const dot = event.target.closest('[data-hero-v2-dot]');
      if (dot) this.goTo(Number(dot.dataset.slideIndex), true);
    };

    this.handleKeydown = (event) => {
      if (event.key === 'ArrowLeft') this.goTo(this.currentIndex - 1, true);
      if (event.key === 'ArrowRight') this.goTo(this.currentIndex + 1, true);
    };

    this.handleMouseEnter = () => {
      this.isHovered = true;
      this.syncAutoplay();
    };

    this.handleMouseLeave = () => {
      this.isHovered = false;
      this.syncAutoplay();
    };

    this.handleFocusIn = () => {
      this.hasFocus = true;
      this.syncAutoplay();
    };

    this.handleFocusOut = (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.hasFocus = false;
      this.syncAutoplay();
    };

    this.handleTouchStart = (event) => {
      this.touchStartX = event.changedTouches[0]?.clientX ?? null;
    };

    this.handleTouchEnd = (event) => {
      if (this.touchStartX === null) return;
      const currentX = event.changedTouches[0]?.clientX ?? this.touchStartX;
      const distance = currentX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(distance) < 50) return;
      this.goTo(distance > 0 ? this.currentIndex - 1 : this.currentIndex + 1, true);
    };

    this.handleVisibilityChange = () => {
      this.syncVideos();
      this.syncAutoplay();
    };

    this.handleMotionChange = () => {
      this.syncVideos();
      this.syncAutoplay();
    };

    this.handleViewportChange = () => {
      this.querySelectorAll('[data-hero-v2-video]').forEach((video) => video.load());
      this.syncSize();
      this.syncVideos();
    };

    this.handleBlockSelect = (event) => {
      const slide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!slide) return;
      this.editorPaused = true;
      this.goTo(this.slides.indexOf(slide));
    };

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.motionPreference.addEventListener?.('change', this.handleMotionChange);
    this.mobileViewport.addEventListener?.('change', this.handleViewportChange);

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.syncVideos();
          this.syncAutoplay();
        },
        { threshold: 0.05 }
      );
      this.visibilityObserver.observe(this);
    }

    this.sizeObserver = new ResizeObserver(() => this.syncSize());
    this.sizeObserver.observe(this);
    this.querySelectorAll('.hero-v2__content').forEach((content) => this.sizeObserver.observe(content));
    this.setActiveSlide(this.currentIndex, false);
    this.syncAutoplay();
  }

  disconnectedCallback() {
    window.clearTimeout(this.autoplayTimer);
    this.visibilityObserver?.disconnect();
    this.sizeObserver?.disconnect();
    this.motionPreference?.removeEventListener?.('change', this.handleMotionChange);
    this.mobileViewport?.removeEventListener?.('change', this.handleViewportChange);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('touchstart', this.handleTouchStart);
    this.removeEventListener('touchend', this.handleTouchEnd);
    this.querySelectorAll('[data-hero-v2-video]').forEach((video) => video.pause());
  }

  normalizeIndex(index) {
    if (this.loop) return (index + this.slides.length) % this.slides.length;
    return Math.min(Math.max(index, 0), this.slides.length - 1);
  }

  syncSize() {
    const mobile = this.mobileViewport.matches;
    const mode = mobile ? this.dataset.mobileSizing : this.dataset.desktopSizing;
    const referenceFrame = this.dataset.frameLayout === 'design';
    if (mode === 'manual' && !referenceFrame) {
      this.style.removeProperty('--hero-v2-auto-height');
      this.style.removeProperty('--hero-v2-content-height');
      return;
    }
    const slide = this.slides[this.currentIndex];
    const mediaRatio = mobile ? slide.dataset.mobileRatio
      : window.matchMedia('(max-width: 1199px)').matches ? slide.dataset.tabletRatio : slide.dataset.desktopRatio;
    const ratio = referenceFrame ? (mobile ? 360 / 480 : 1840 / 790) : Number(mediaRatio) || 16 / 9;
    const width = this.getBoundingClientRect().width;
    if (!width || ratio <= 0) return;
    // Preserve the chosen frame or media ratio, allowing long copy to expand it.
    const imageHeight = `${width / ratio}px`;
    const contentHeight = `${Math.ceil(Math.max(...this.slides.map((item) => {
      const content = item.querySelector('.hero-v2__content');
      const positioner = item.querySelector('.hero-v2__positioner');
      if (!content || !positioner) return 0;
      const spacing = getComputedStyle(positioner);
      return content.getBoundingClientRect().height + parseFloat(spacing.paddingTop) + parseFloat(spacing.paddingBottom);
    })))}px`;
    if (this.style.getPropertyValue('--hero-v2-auto-height') !== imageHeight) {
      this.style.setProperty('--hero-v2-auto-height', imageHeight);
    }
    if (this.style.getPropertyValue('--hero-v2-content-height') !== contentHeight) {
      this.style.setProperty('--hero-v2-content-height', contentHeight);
    }
  }

  goTo(index, fromUser = false) {
    if (this.slides.length < 2) return;
    const normalizedIndex = this.normalizeIndex(index);
    if (normalizedIndex === this.currentIndex && !this.loop) return;
    if (fromUser) window.clearTimeout(this.autoplayTimer);
    this.setActiveSlide(normalizedIndex, true);
    this.syncAutoplay();
  }

  setActiveSlide(index, announce = true) {
    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });

    if (this.dataset.effect === 'slide' && this.track) {
      this.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
    }

    if (!this.loop) {
      if (this.previousButton) this.previousButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
    }

    if (announce && this.status) {
      this.status.textContent = this.statusTemplate
        .replace('__index__', String(index + 1))
        .replace('__length__', String(this.slides.length));
    }

    this.syncSize();
    this.syncVideos();
  }

  syncVideos() {
    this.slides.forEach((slide, index) => {
      const videos = slide.querySelectorAll('[data-hero-v2-video]');
      videos.forEach((video) => {
        const shouldPlay =
          index === this.currentIndex &&
          this.isVisible &&
          !document.hidden &&
          !this.motionPreference?.matches;

        if (!shouldPlay) {
          video.pause();
          return;
        }

        video.play().catch(() => {
          // Poster images remain visible when browser autoplay is unavailable.
        });
      });
    });
  }

  syncAutoplay() {
    window.clearTimeout(this.autoplayTimer);
    const canAdvance = this.loop || this.currentIndex < this.slides.length - 1;
    const shouldRun =
      this.autoplayEnabled &&
      canAdvance &&
      !this.editorPaused &&
      !this.motionPreference?.matches &&
      this.isVisible &&
      !document.hidden &&
      !this.hasFocus &&
      !(this.pauseOnHover && this.isHovered);

    this.dataset.autoplayRunning = String(shouldRun);
    if (!shouldRun) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.setActiveSlide(this.normalizeIndex(this.currentIndex + 1), true);
      this.syncAutoplay();
    }, this.autoplayDelay);
  }
}

if (!customElements.get('hero-banner-v2')) {
  customElements.define('hero-banner-v2', HeroBannerV2);
}
